import type { Route } from '#/global'
import type { RouteRecordRaw } from 'vue-router'
import apiApp from '@/api/modules/app'
import { createBackendGuideRouteRecords } from '@/onboarding/routes'
import type { BackendGuideRouteRecord } from '@/onboarding/routes'
import { i18n } from '@/locales'
import { resolveLocalMenuIcon } from '@/router/menuIconMap'
import { systemRoutes } from '@/router/routes'
import { titleKeyMap } from '@/store/modules/titleKeyMap'
import { resolveRoutePath } from '@/utils'
import { cloneDeep } from 'es-toolkit'
import useSettingsStore from './settings'
import useTabbarStore from './tabbar'

const useRouteStore = defineStore(
  // 唯一ID
  'route',
  () => {
    const settingsStore = useSettingsStore()
    const tabbarStore = useTabbarStore()

    const isGenerate = ref(false)
    const routesRaw = ref<Route.recordMainRaw[]>([])
    // 后端接口原始路由及其教程索引。教程页面跳转只读取这里，不再使用写死 URL。
    const backendRoutesRaw = ref<any[]>([])
    const backendGuideRoutes = ref<BackendGuideRouteRecord[]>([])
    const backendGuideRoutesStatus = ref<'idle' | 'loading' | 'indexed' | 'registering' | 'ready' | 'error'>('idle')
    const backendGuideRegisteredRouteKeys = ref<string[]>([])
    const filesystemRoutesRaw = ref<RouteRecordRaw[]>([])
    const currentRemoveRoutes = ref<(() => void)[]>([])

    // 将多层嵌套路由处理成两层，保留顶层和最子层路由，中间层级将被拍平
    function flatAsyncRoutes<T extends RouteRecordRaw>(route: T): T {
      if (route.children) {
        route.children = flatAsyncRoutesRecursive(route.children, [{
          path: route.path,
          title: route.meta?.title,
          icon: route.meta?.icon,
          activeIcon: route.meta?.activeIcon,
          hide: !route.meta?.breadcrumb && route.meta?.breadcrumb === false,
        }], route.path)
      }
      return route
    }
    function flatAsyncRoutesRecursive(routes: RouteRecordRaw[], breadcrumb: Route.breadcrumb[] = [], baseUrl = ''): RouteRecordRaw[] {
      const res: RouteRecordRaw[] = []
      routes.forEach((route) => {
        if (route.children) {
          const childrenBaseUrl = resolveRoutePath(baseUrl, route.path)
          const tmpBreadcrumb = cloneDeep(breadcrumb)
          tmpBreadcrumb.push({
            path: childrenBaseUrl,
            title: route.meta?.title,
            icon: route.meta?.icon,
            activeIcon: route.meta?.activeIcon,
            hide: !route.meta?.breadcrumb && route.meta?.breadcrumb === false,
          })
          const tmpRoute = cloneDeep(route)
          tmpRoute.path = childrenBaseUrl
          if (!tmpRoute.meta) {
            tmpRoute.meta = {}
          }
          tmpRoute.meta.breadcrumbNeste = tmpBreadcrumb
          delete tmpRoute.children
          res.push(tmpRoute)
          const childrenRoutes = flatAsyncRoutesRecursive(route.children, tmpBreadcrumb, childrenBaseUrl)
          childrenRoutes.forEach((item) => {
            // 如果 path 一样则覆盖，因为子路由的 path 可能设置为空，导致和父路由一样，直接注册会提示路由重复
            if (res.some(v => v.path === item.path)) {
              res.forEach((v, i) => {
                if (v.path === item.path) {
                  res[i] = item
                }
              })
            }
            else {
              res.push(item)
            }
          })
        }
        else {
          const tmpRoute = cloneDeep(route)
          tmpRoute.path = resolveRoutePath(baseUrl, tmpRoute.path)
          // 处理面包屑导航
          const tmpBreadcrumb = cloneDeep(breadcrumb)
          tmpBreadcrumb.push({
            path: tmpRoute.path,
            title: tmpRoute.meta?.title,
            icon: tmpRoute.meta?.icon,
            activeIcon: tmpRoute.meta?.activeIcon,
            hide: !tmpRoute.meta?.breadcrumb && tmpRoute.meta?.breadcrumb === false,
          })
          if (!tmpRoute.meta) {
            tmpRoute.meta = {}
          }
          tmpRoute.meta.breadcrumbNeste = tmpBreadcrumb
          res.push(tmpRoute)
        }
      })
      return res
    }
    // 扁平化路由（将三级及以上路由数据拍平成二级）
    const flatRoutes = computed(() => {
      const returnRoutes: RouteRecordRaw[] = []
      if (settingsStore.settings.app.routeBaseOn !== 'filesystem') {
        if (routesRaw.value) {
          routesRaw.value.forEach((item) => {
            const tmpRoutes = cloneDeep(item.children) as RouteRecordRaw[]
            tmpRoutes.map((v) => {
              if (!v.meta) {
                v.meta = {}
              }
              v.meta.auth = item.meta?.auth ?? v.meta?.auth
              return v
            })
            returnRoutes.push(...tmpRoutes)
          })
          returnRoutes.forEach(item => flatAsyncRoutes(item))
        }
      }
      else {
        returnRoutes.push(...cloneDeep(filesystemRoutesRaw.value) as RouteRecordRaw[])
      }
      return returnRoutes
    })
    const flatSystemRoutes = computed(() => {
      const routes = [...systemRoutes]
      routes.forEach(item => flatAsyncRoutes(item))
      return routes
    })

    // 路由排序，sort 越大越靠前
    function sortAsyncRoutes<T extends Route.recordMainRaw[] | RouteRecordRaw[]>(routes: T): T {
      routes.sort((a, b) => (b.meta?.sort ?? 0) - (a.meta?.sort ?? 0))
      routes.forEach((route) => {
        if (route.children) {
          route.children = sortAsyncRoutes(route.children)
        }
      })
      return routes
    }

    // 将设置 meta.singleMenu 的一级路由转换成二级路由
    function convertSingleRoutes<T extends Route.recordMainRaw[]>(routes: T): T {
      routes.map((route) => {
        if (route.children) {
          route.children.forEach((item, index, arr) => {
            if (item.meta?.singleMenu) {
              arr[index] = {
                ...item,
                component: () => import('@/layouts/index.vue'),
                children: [
                  {
                    path: '',
                    name: item.name,
                    component: item.component,
                    meta: {
                      title: item.meta.title,
                      menu: false,
                      breadcrumb: false,
                      // 单菜单路由转换后仍保留原始页面组件路径，供新手引导定位真实页面。
                      onboardingComponentPath: item.meta.onboardingComponentPath,
                    },
                  },
                ],
              } as RouteRecordRaw
              delete arr[index].name
              delete arr[index].meta!.singleMenu
            }
          })
        }
        return route
      })
      return routes
    }

    // 生成路由（前端生成）
    function generateRoutesAtFront(asyncRoutes: Route.recordMainRaw[]) {
      // 设置 routes 数据
      routesRaw.value = convertSingleRoutes(sortAsyncRoutes(cloneDeep(asyncRoutes) as any))
      isGenerate.value = true
      // 加载常驻标签页
      if (settingsStore.settings.tabbar.enable) {
        tabbarStore.initPermanentTab()
      }
    }
    function normalizeBackendViewPath(componentPath: unknown) {
      return String(componentPath || '')
        .split('?')[0]
        .replace(/\\/g, '/')
        .replace(/^@\/views\//i, '')
        .replace(/^src\/views\//i, '')
        .replace(/^\.\.\/\.\.\/views\//i, '')
        .replace(/^views\//i, '')
        .replace(/^\/+/, '')
        .replace(/\.vue$/i, '')
        .replace(/\/index$/i, '')
        .replace(/\/+$/, '')
    }

    function resolveBackendViewComponent(componentPath: string, views: Record<string, any>) {
      const normalized = normalizeBackendViewPath(componentPath)
      if (!normalized) {
        return undefined
      }
      const candidates = [
        `../../views/${normalized}.vue`,
        `../../views/${normalized}/index.vue`,
      ]
      const direct = candidates.map(key => views[key]).find(Boolean)
      if (direct) {
        return direct
      }

      const normalizedLower = normalized.toLowerCase()
      const matchedKey = Object.keys(views).find((key) => {
        return normalizeBackendViewPath(key).toLowerCase() === normalizedLower
      })
      return matchedKey ? views[matchedKey] : undefined
    }

    // 格式化后端路由数据
    function formatBackRoutes(routes: any, views = import.meta.glob('../../views/**/*.vue')): Route.recordMainRaw[] {
      return routes.map((route: any) => {
        if (route.meta) {
          const interfaceIcon = route.meta.icon
          const interfaceActiveIcon = route.meta.activeIcon
          const localIcon = resolveLocalMenuIcon(route.meta.title, route.component)

          // 旧逻辑保留：菜单图标完全沿用接口返回的 icon 和 activeIcon。
          // route.meta.icon = interfaceIcon
          // route.meta.activeIcon = interfaceActiveIcon

          // 新逻辑说明：有本地映射时使用压缩包图标；没有匹配时继续使用接口图标。
          // 本地图标通过 currentColor 跟随菜单激活颜色，因此普通和激活状态使用同一个 SVG。
          route.meta.icon = localIcon ?? interfaceIcon
          route.meta.activeIcon = localIcon ?? interfaceActiveIcon
        }
        switch (route.component) {
          case 'Layout':
            route.component = () => import('@/layouts/index.vue')
            break
          default:
            if (route.component) {
              if (!route.meta) {
                route.meta = {}
              }
              // 新手引导通过后端原始组件路径定位动态路由，避免依赖接口返回的 URL 命名。
              const rawComponentPath = route.component
              route.meta.onboardingComponentPath = normalizeBackendViewPath(rawComponentPath)
              route.meta.onboardingRawComponentPath = rawComponentPath
              route.component = resolveBackendViewComponent(rawComponentPath, views)
            }
            else {
              delete route.component
            }
        }
        if (route.meta && route.meta.title) {
          const i18nKey = titleKeyMap[route.meta.title]
          if (i18nKey) {
            // 使用i18n实例的真实翻译方法
            route.meta.title = i18n.global.t(`app.menu.${i18nKey}`)
          }
        }
        if (route.children) {
          route.children = formatBackRoutes(route.children, views)
        }
        return route
      })
    }
    // 生成路由（后端获取）
    async function generateRoutesAtBack() {
      backendGuideRoutesStatus.value = 'loading'
      await apiApp.routeList().then((res) => {
        // 必须在 formatBackRoutes 替换 component 之前保存接口原始值。
        // 教程索引中的 fullPath/component/name/title/menuId 均来源于本次接口响应。
        backendRoutesRaw.value = cloneDeep(Array.isArray(res.data) ? res.data : [])
        backendGuideRoutes.value = createBackendGuideRouteRecords(backendRoutesRaw.value)
        // 此时只完成 routeList 请求和动态路由转换，Vue Router 尚未 addRoute。
        backendGuideRoutesStatus.value = 'indexed'
        backendGuideRegisteredRouteKeys.value = []
        // 设置 routes 数据
        routesRaw.value = convertSingleRoutes(sortAsyncRoutes(formatBackRoutes(cloneDeep(backendRoutesRaw.value)) as any))
        isGenerate.value = true
        // 加载常驻标签页
        if (settingsStore.settings.tabbar.enable) {
          tabbarStore.initPermanentTab()
        }
      }).catch((error) => {
        backendGuideRoutesStatus.value = 'error'
        // 阻止 guards 在 routeList 失败后继续进入 router.addRoute 并把状态误标为 ready。
        throw error
      })
    }
    // 生成路由（文件系统生成）
    function generateRoutesAtFilesystem(asyncRoutes: RouteRecordRaw[]) {
      // 设置 routes 数据
      filesystemRoutesRaw.value = convertSingleRoutes(sortAsyncRoutes(cloneDeep(asyncRoutes) as any))
      isGenerate.value = true
      // 加载常驻标签页
      if (settingsStore.settings.tabbar.enable) {
        tabbarStore.initPermanentTab()
      }
    }
    function normalizeGuideRegisteredPath(value: unknown) {
      const path = String(value || '')
        .split(/[?#]/)[0]
        .replace(/\\/g, '/')
        .replace(/\/{2,}/g, '/')
      if (!path) return ''
      return path.length > 1 ? path.replace(/\/$/, '') : path
    }

    function guideRouteKey(name: unknown, path: unknown) {
      return `${String(name || '')}::${normalizeGuideRegisteredPath(path)}`
    }

    // 只表示新手引导路由正在注册，不改变原动态路由业务逻辑。
    function markBackendGuideRoutesRegistering() {
      if (backendGuideRoutesStatus.value === 'indexed') {
        backendGuideRoutesStatus.value = 'registering'
      }
    }

    // 必须在 router.addRoute 全部执行完成后调用。
    function markBackendGuideRoutesReady(registeredRoutes: Array<{ name?: unknown, path?: unknown }>) {
      backendGuideRegisteredRouteKeys.value = registeredRoutes.map(route => guideRouteKey(route.name, route.path))
      backendGuideRoutesStatus.value = 'ready'
    }

    function markBackendGuideRoutesError() {
      backendGuideRegisteredRouteKeys.value = []
      backendGuideRoutesStatus.value = 'error'
    }

    function isBackendGuideRouteRegistered(record: BackendGuideRouteRecord) {
      if (backendGuideRoutesStatus.value !== 'ready') return false
      const nameKey = record.name ? guideRouteKey(record.name, record.fullPath) : ''
      const normalizedPath = normalizeGuideRegisteredPath(record.fullPath)
      return backendGuideRegisteredRouteKeys.value.some((key) => {
        const [registeredName, registeredPath] = key.split('::')
        return (record.name && registeredName === record.name)
          || registeredPath === normalizedPath
          || (nameKey && key === nameKey)
      })
    }

    // 记录 accessRoutes 路由，用于登出时删除路由
    function setCurrentRemoveRoutes(routes: (() => void)[]) {
      currentRemoveRoutes.value = routes
    }
    // 清空动态路由
    function removeRoutes() {
      isGenerate.value = false
      routesRaw.value = []
      backendRoutesRaw.value = []
      backendGuideRoutes.value = []
      backendGuideRoutesStatus.value = 'idle'
      backendGuideRegisteredRouteKeys.value = []
      filesystemRoutesRaw.value = []
      currentRemoveRoutes.value.forEach((removeRoute) => {
        removeRoute()
      })
      currentRemoveRoutes.value = []
    }

    return {
      isGenerate,
      routesRaw,
      backendRoutesRaw,
      backendGuideRoutes,
      backendGuideRoutesStatus,
      backendGuideRegisteredRouteKeys,
      currentRemoveRoutes,
      flatRoutes,
      flatSystemRoutes,
      generateRoutesAtFront,
      generateRoutesAtBack,
      generateRoutesAtFilesystem,
      markBackendGuideRoutesRegistering,
      markBackendGuideRoutesReady,
      markBackendGuideRoutesError,
      isBackendGuideRouteRegistered,
      setCurrentRemoveRoutes,
      removeRoutes,
    }
  },
)

export default useRouteStore
